
import {mapCultureType} from '../WorkforceProfiles/mapCultureType';
import {toPercentScore} from '../WorkforceProfiles/utils';
import type {ApiWorkforceProfileRecord} from '../WorkforceProfiles/workforceProfileApiTypes';

import {mapBilingualStringList} from './mapBilingualStringList';
import {mapScaleConfiguration} from './mapInferredCultural';
import {mapRequiredLanguageProfile} from './mapRequiredLanguageProfile';
import {ApiSerializer} from '../../../../../serializers/ApiSerializer';
import {
  isWorkforceAlignmentLabel,
  type WorkforceProfileCulturalFitAnalysis,
  type WorkforceProfileCulturalFitPillarDescription,
  type WorkforceProfileCulturalFitPillarScore,
  type WorkforceProfileCulturalFitSummary,
  type WorkforceProfileCulturalFitTopQuestion,
} from '../../types/WorkforceProfileTypes';

const mapCulturalFitSummary = (
  raw: ApiWorkforceProfileRecord | undefined
): WorkforceProfileCulturalFitSummary | null => {
  if (!raw) {
    return null;
  }

  const fitScore = toPercentScore(raw.fitScore as number | undefined);
  const averageCompanyEmployeeFit =
    toPercentScore(raw.companyAverageCultureAnalysisInternalFit as number | undefined) ??
    toPercentScore(raw.averageCompanyEmployeeFit as number | undefined);
  const averageCompanyApplicantFit =
    toPercentScore(raw.companyAverageWorkforceProfileFit as number | undefined) ??
    toPercentScore(raw.averageCompanyApplicantFit as number | undefined);

  if (fitScore == null && averageCompanyEmployeeFit == null && averageCompanyApplicantFit == null) {
    return null;
  }

  return {fitScore, averageCompanyEmployeeFit, averageCompanyApplicantFit};
};

const mapCulturalFitPillarData = (raw: unknown): WorkforceProfileCulturalFitPillarScore[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((entry) => {
      const pillar = (
        entry && typeof entry === 'object' ? ApiSerializer.toCamelCase(entry) : entry
      ) as ApiWorkforceProfileRecord;
      const title = mapRequiredLanguageProfile(pillar.title);
      const score = pillar.score as ApiWorkforceProfileRecord | undefined;
      const companyScore = Number(score?.company);
      const workforceProfileScore = Number(score?.workforceProfile);

      if (!title || Number.isNaN(companyScore) || Number.isNaN(workforceProfileScore)) {
        return null;
      }

      return {title, companyScore, workforceProfileScore};
    })
    .filter((pillar): pillar is WorkforceProfileCulturalFitPillarScore => pillar != null);
};

const mapCulturalFitTopQuestions = (raw: unknown): WorkforceProfileCulturalFitTopQuestion[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((entry) => {
      const question = (
        entry && typeof entry === 'object' ? ApiSerializer.toCamelCase(entry) : entry
      ) as ApiWorkforceProfileRecord;
      const title = mapRequiredLanguageProfile(question.title);
      const scaleConfiguration = mapScaleConfiguration(
        question.scaleConfiguration as ApiWorkforceProfileRecord | undefined
      );
      const companyValue = Number(question.companyValue);
      const workforceProfileValue = Number(question.workforceProfileValue);

      const overlapLabelRaw = String(question.overlapLabel ?? question.overlap_label ?? '').toLowerCase();

      if (
        !title ||
        !scaleConfiguration ||
        Number.isNaN(companyValue) ||
        Number.isNaN(workforceProfileValue) ||
        !isWorkforceAlignmentLabel(overlapLabelRaw)
      ) {
        return null;
      }

      const overlapRaw = question.overlap;

      return {
        resourceId: String(question.resourceId ?? ''),
        humanReadableIdentifier: String(question.humanReadableIdentifier ?? ''),
        title,
        description: mapRequiredLanguageProfile(question.description),
        overlap: overlapRaw == null || Number.isNaN(Number(overlapRaw)) ? null : Number(overlapRaw),
        overlapLabel: overlapLabelRaw,
        companyValue,
        workforceProfileValue,
        scaleConfiguration,
      };
    })
    .filter((question): question is WorkforceProfileCulturalFitTopQuestion => question != null);
};

const mapCulturalFitPillarDescriptions = (raw: unknown): WorkforceProfileCulturalFitPillarDescription[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((entry) => {
      const pillar = (
        entry && typeof entry === 'object' ? ApiSerializer.toCamelCase(entry) : entry
      ) as ApiWorkforceProfileRecord;
      const pillarTitle = mapRequiredLanguageProfile(pillar.pillarTitle);

      if (!pillarTitle) {
        return null;
      }

      const overlapRaw = pillar.overlap;

      return {
        pillarTitle,
        overlap: overlapRaw == null || Number.isNaN(Number(overlapRaw)) ? null : Number(overlapRaw),
        evaluations: mapBilingualStringList(pillar.evaluations),
        opportunities: mapBilingualStringList(pillar.opportunities),
        risks: mapBilingualStringList(pillar.risks),
        whatToLookFor: mapBilingualStringList(pillar.whatToLookFor),
        interviewQuestions: mapBilingualStringList(pillar.interviewQuestions),
      };
    })
    .filter((pillar): pillar is WorkforceProfileCulturalFitPillarDescription => pillar != null);
};

export const mapCulturalFitAnalysis = (
  raw: ApiWorkforceProfileRecord | undefined
): WorkforceProfileCulturalFitAnalysis | undefined => {
  if (!raw || !raw.available) {
    return undefined;
  }

  const cultureFit = mapCulturalFitSummary(raw.cultureFit as ApiWorkforceProfileRecord | undefined);
  const cultureType = mapCultureType(raw.cultureType as ApiWorkforceProfileRecord | undefined);
  const pillarData = mapCulturalFitPillarData(raw.pillarData);
  const topQuestions = mapCulturalFitTopQuestions(raw.topQuestions);
  const pillarDescriptions = mapCulturalFitPillarDescriptions(raw.pillarCulturalFitDescriptions);

  return {
    available: true,
    cultureFit,
    cultureType,
    pillarData,
    topQuestions,
    pillarDescriptions,
  };
};
